import { dataIndexListPath, type DataIndexListType } from './urls';

/**
 * Where each list was left, so that coming back to it resumes it.
 *
 * The page this replaces gets this from Ember's sticky query params: the list
 * route's params are cached per `:type` for as long as the app is loaded, and
 * every named link to a list — the tabs (`type/list/template.gts`, via
 * `urlFor`), the `data-index` landing redirect (`index/route.ts`) and so the
 * item page's "Data index" crumb — is built with that type's cached values.
 * So Attributes reopens on the object type it was left on, Events on the
 * in-use view, and each keeps its own search, filters and page. Nothing
 * resets it short of reloading the app, which is what a module-level map
 * gives here too.
 *
 * Keyed by workspace as well as list: Ember's cache key includes every
 * parent route's params, the workspace among them.
 */
const rememberedSearches = new Map<string, string>();

function memoryKey(workspaceId: string, type: DataIndexListType): string {
  return `${workspaceId}:${type}`;
}

/** Records the query a list is showing, as the URL spells it (`?…`, or empty). */
export function rememberListSearch(workspaceId: string, type: DataIndexListType, search: string): void {
  rememberedSearches.set(memoryKey(workspaceId, type), search);
}

/** A list's URL, carrying the query it was last left on. */
export function rememberedListPath(workspaceId: string, type: DataIndexListType): string {
  return `${dataIndexListPath(workspaceId, type)}${rememberedSearches.get(memoryKey(workspaceId, type)) ?? ''}`;
}

/**
 * A list's URL for a hand-off, which carries the current query across itself:
 * the list where it was left when there is no query to carry, and the bare
 * list otherwise, so the two are never joined into one malformed query.
 */
export function resumedListPath(workspaceId: string, type: DataIndexListType, carriedSearch: string): string {
  return carriedSearch === '' ? rememberedListPath(workspaceId, type) : dataIndexListPath(workspaceId, type);
}

/** Forgets every list, as a reload of the app would. */
export function forgetRememberedLists(): void {
  rememberedSearches.clear();
}
